import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Pencil } from "lucide-react";
import { SubmitError } from "./form-utils";

// One reusable step interface for Sell, Investors and Buyers.
// steps: [{ id, title, helper, render(ctx), validate?(data) -> {field: msg} }]
// review(data) -> [{ label, value, stepId }]

export function StepShell({ index, total, title, helper, children }) {
  return (
    <div>
      <div className="overline" data-testid="wizard-step-counter">Step {index + 1} of {total}</div>
      <h2 className="font-display text-2xl md:text-3xl text-white mt-3">{title}</h2>
      {helper && <p className="text-sm text-neutral-400 mt-2">{helper}</p>}
      <div className="mt-8">{children}</div>
    </div>
  );
}

export default function StepWizard({ steps, data, setField, review, consentLabel, onSubmit, submitting, submitError, submitLabel = "Submit", testIdPrefix = "wizard", intro, storageKey }) {
  const [index, setIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const topRef = useRef(null);
  const first = useRef(true);
  const total = steps.length + 1; // + review
  const isReview = index === steps.length;
  const current = steps[Math.min(index, steps.length - 1)];

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [index]);

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch { /* ignore */ }
  }, [data, storageKey]);

  const validateCurrent = () => {
    const e = isReview ? (data.consent ? {} : { consent: "Please agree to be contacted" }) : (current.validate ? current.validate(data) : {});
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const next = () => { if (validateCurrent()) setIndex((i) => Math.min(i + 1, steps.length)); };
  const back = () => { setErrors({}); setIndex((i) => Math.max(0, i - 1)); };
  const jumpTo = (stepId) => { setErrors({}); setIndex(Math.max(0, steps.findIndex((s) => s.id === stepId))); };
  const submit = () => { if (validateCurrent()) onSubmit(); };

  const progress = Math.round((index / (total - 1)) * 100);
  const ctx = { data, set: setField, errors };

  return (
    <div ref={topRef} className="card-frame p-6 md:p-10" data-testid={`${testIdPrefix}-form`}>
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-neutral-500 mb-2">
          <span>{isReview ? "Review" : current.label || current.title}</span>
          <span data-testid={`${testIdPrefix}-progress`}>{progress}%</span>
        </div>
        <div className="h-1 bg-white/5 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-[#C19B6C] transition-[width]" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {index === 0 && intro}

      {!isReview && (
        <StepShell index={index} total={total} title={current.title} helper={current.helper}>
          {current.render(ctx)}
        </StepShell>
      )}

      {isReview && (
        <StepShell index={index} total={total} title="Review and submit" helper="Check your answers. Use Edit to change anything before you submit.">
          <dl className="divide-y divide-white/5 text-sm" data-testid={`${testIdPrefix}-review`}>
            {review(data).filter((r) => r.value !== undefined && r.value !== null && r.value !== "").map((r) => (
              <div key={`${r.stepId}-${r.label}`} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">{r.label}</dt>
                  <dd className="text-neutral-200 break-words whitespace-pre-line mt-0.5">{r.value}</dd>
                </div>
                <button type="button" onClick={() => jumpTo(r.stepId)} className="text-xs text-[#C19B6C] hover:text-white inline-flex items-center gap-1 shrink-0" data-testid={`${testIdPrefix}-edit-${r.stepId}`}>
                  <Pencil size={12} /> Edit
                </button>
              </div>
            ))}
          </dl>
          <label className="mt-8 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={!!data.consent} onChange={(e) => setField("consent", e.target.checked)} className="mt-1 accent-[#C19B6C]" data-testid={`${testIdPrefix}-consent`} />
            <span className="text-xs text-neutral-400 leading-relaxed">{consentLabel}</span>
          </label>
          {errors.consent && <div className="text-xs text-red-400 mt-2" role="alert">{errors.consent}</div>}
          <div className="mt-6"><SubmitError error={submitError} pending={submitting} onRetry={submit} /></div>
        </StepShell>
      )}

      <div className="mt-10 flex items-center justify-between gap-3">
        <button type="button" onClick={back} disabled={index === 0 || submitting} className="btn-ghost px-5 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid={`${testIdPrefix}-back`}>
          <ArrowLeft size={14} /> Back
        </button>
        {!isReview ? (
          <button type="button" onClick={next} className="btn-gold px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2" data-testid={`${testIdPrefix}-next`}>
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={submitting} className="btn-gold px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${testIdPrefix}-submit`}>
            {submitting && <Loader2 className="animate-spin" size={14} />}
            {submitting ? "Submitting…" : submitLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export function useWizardSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  return { submitting, setSubmitting, submitError, setSubmitError };
}
